import { useFacade } from "@/facades";
import { AxiosError } from "axios";
import { asyncNoop, noop } from "es-toolkit";
import Swal from "sweetalert2";
import type { Ref } from "vue";

export class RequestController<Res = never> {
    private successFn: (data: Res) => unknown;
    private failureFn: (message: string) => unknown;
    private doneFn: () => unknown = asyncNoop;

    constructor(
        private data: Ref<{ status: number; message: string; data: Res }>,
        private executeFn: () => Promise<unknown>,
    ) {
        this.successFn = noop;
        this.failureFn = noop;
    }

    public standardHooks() {
        this.successFn = () =>
            Swal.fire({
                icon: "success",
                title: "Berhasil",
            });

        this.failureFn = (message: string) =>
            Swal.fire({
                icon: "error",
                title: "Gagal",
                text: message,
            });

        return this;
    }

    public clearHooks() {
        this.successFn = noop;
        this.failureFn = noop;
        return this;
    }

    public onSuccess(successFn: (data: Res) => unknown) {
        this.successFn = successFn;
        return this;
    }

    public onFailure(failureFn: (message: string) => unknown) {
        this.failureFn = failureFn;
        return this;
    }

    public onDone(doneFn: () => unknown) {
        this.doneFn = doneFn;
        return this;
    }

    public async execute(isLoading?: Ref<boolean>): Promise<Res | undefined> {
        try {
            await this.makeRequest(isLoading);
            this.handleServerErrors();

            const response = this.data.value;
            this.successFn(response.data);
            return response.data;
        } catch (e) {
            if (e instanceof AxiosError) {
                if (e.status === 503) {
                    if (!import.meta.env.SSR) {
                        Swal.fire({
                            icon: "warning",
                            title: "PERHATIAN",
                            text: "Server sedang berada dalam mode maintenance.",
                        }).then(() => {
                            const f = useFacade();
                            if (f.profile.kind !== "dev") {
                                f.token.logout();
                            } else {
                                window.location.replace("/devtools/maintenance");
                            }
                        });
                    }
                } else if (e.status === 401) {
                    if (!import.meta.env.SSR) {
                        Swal.fire({
                            icon: "error",
                            title: "PERHATIAN",
                            html: "Sesi anda telah habis.<br>Silakan login kembali untuk melanjutkan.",
                        }).then(() => {
                            const f = useFacade();
                            window.location.replace("/signin");
                            f.token.logout();
                        });
                    }
                } else {
                    switch (e.code) {
                        case AxiosError.ERR_NETWORK: // this.failureFn("Gagal terhubung ke internet.");
                        case AxiosError.ETIMEDOUT:
                        case AxiosError.ERR_CANCELED:
                        case AxiosError.ECONNABORTED:
                            this.failureFn("Gagal terhubung ke server.");
                            break;
                        default:
                            console.error(e.response?.data.message);
                            this.failureFn(e.response?.data.message);
                            break;
                    }
                }
            } else {
                const error = e as Error;
                console.error(error.message);
                this.failureFn(error.message);
            }
        } finally {
            this.doneFn();
            if (isLoading !== undefined) {
                isLoading.value = false;
            }
        }

        return undefined;
    }

    private async makeRequest(isLoading: Ref<boolean> | undefined) {
        if (isLoading !== undefined) {
            const progress = setTimeout(() => (isLoading.value = true), 250);

            try {
                await this.executeFn();
            } finally {
                clearTimeout(progress);
                isLoading.value = false;
            }
        } else {
            await this.executeFn();
        }
    }

    private handleServerErrors() {
        const response = this.data.value;

        if (!import.meta.env.SSR && response.status === 401) {
            window.location.replace("/signin");
            throw new Error(response.message);
        }

        if (response.status !== 1) {
            // Error from server.
            throw new Error(response.message);
        }
    }
}
